import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import styles from "./UpdateDeliveries.module.css";
import axiosInstance from "../../../Configuration/axiosInstance";
import SuccessDialogue from "../../../Components/Shared/SuccessDialog/SuccessDialog";
import ProductsModal from "../../../Components/ProductsModal/ProductsModal";
import LoadingSpinner from "../../../Components/Shared/Loader/Loader";

const UpdateDeliveries = () => {
  const location = useLocation();
  const id = location?.state?._id;

  // ✅ UPDATED: Handle new order structure from location.state
  const initialOrders = location?.state?.order
    ? location.state.order.map((orderItem) => {
        // If it's already in new structure, extract the order
        if (orderItem.orderId) {
          return orderItem.orderId;
        }
        // If it's still in old structure, use as is
        return orderItem;
      })
    : [];

  // State for current delivery data
  const [checkboxItems, setCheckBoxItems] = useState([]);
  const [order, setOrder] = useState(initialOrders);
  const [run, setRun] = useState(location?.state?.run || "");
  const [vehicle, setVehicle] = useState(location?.state?.vehicle || "");
  const [comments, setComments] = useState(location?.state?.comments || "");
  const [deliveryDate, setDeliveryDate] = useState(
    location?.state?.date
      ? new Date(location.state.date).toISOString().split("T")[0]
      : ""
  );

  // State for assigned users
  const [assignedUsers, setAssignedUsers] = useState(
    location?.state?.assignedUsers?.map((user) => user._id || user) || []
  );
  const [primaryDriver, setPrimaryDriver] = useState(
    location?.state?.primaryDriver?._id || location?.state?.primaryDriver || ""
  );
  const [availableUsers, setAvailableUsers] = useState([]);

  // State for adding orders
  const [pendingOrders, setPendingOrders] = useState([]);
  const [date, setDate] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [addToRun, setAddToRun] = useState(false);

  // UI state
  const [show, setShow] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [dialogBox, setDialogBox] = useState(false);

  // Debug log the initial state
  useEffect(() => {
    console.log("=== UpdateDeliveries Debug ===");
    console.log("Location state:", location?.state);
    console.log("Initial orders:", initialOrders);
    console.log("Assigned users:", assignedUsers);
    console.log("Primary driver:", primaryDriver);
  }, []);

  // Fetch available users (delivery drivers)
  useEffect(() => {
    axiosInstance
      .get("/user")
      .then((res) => {
        const deliveryUsers = res.data.data.filter(
          (user) => user.role === "delivery"
        );
        setAvailableUsers(deliveryUsers);
      })
      .catch(() => setAvailableUsers([]));
  }, []);

  // Fetch pending orders for adding to run
  useEffect(() => {
    axiosInstance
      .get(
        `delivery/deliverOrder?page=${page}&limit=${pageSize}&filter=${date}`
      )
      .then((data) => {
        setPendingOrders(data.data.data);
      });
  }, [page, pageSize, date]);

  const handleCheckbox = (e, orderNum) => {
    if (e.target.checked) {
      setCheckBoxItems((prevItems) => [...prevItems, Number(e.target.value)]);
    } else {
      setCheckBoxItems(checkboxItems.filter((i) => i !== orderNum));
    }
  };

  const handleAddToRunClick = () => {
    setAddToRun((prevState) => !prevState);
  };

  const addOrderToRun = () => {
    const selectedOrders = pendingOrders.filter((i) =>
      checkboxItems.includes(i?.orderNumber)
    );
    setOrder((prevOrder) => [...prevOrder, ...selectedOrders]);

    const updatedPendingOrders = pendingOrders.filter(
      (i) => !checkboxItems.includes(i?.orderNumber)
    );
    setCheckBoxItems([]);
    setPendingOrders(updatedPendingOrders);
  };

  const removeFromRun = () => {
    const remainingOrders = order.filter(
      (i) => !checkboxItems.includes(i.orderNumber)
    );
    setOrder(remainingOrders);
    setCheckBoxItems([]);
  };

  const handleAssignedUsersChange = (e) => {
    const options = Array.from(
      e.target.selectedOptions,
      (option) => option.value
    );
    setAssignedUsers(options);
  };

  const handlePrimaryDriverChange = (e) => {
    setPrimaryDriver(e.target.value);
  };

  const updateDelivery = () => {
    setLoading(true);

    // ✅ UPDATED: Send order IDs in correct format for backend
    const updateData = {
      run: run.trim(),
      vehicle: vehicle.trim(),
      date: deliveryDate,
      comments: comments.trim(),
      order: order.map((o) => o._id), // Extract order IDs
      assignedUsers: assignedUsers,
      primaryDriver: primaryDriver || undefined, // Send undefined if empty
    };

    console.log("Updating delivery with data:", updateData);

    axiosInstance
      .put(`delivery/${id}`, updateData)
      .then((res) => {
        setLoading(false);
        console.log("Update response:", res.data);

        // ✅ UPDATED: Handle new order structure in response
        if (res.data.updatedDelivery?.order) {
          const updatedOrders = res.data.updatedDelivery.order.map((orderItem) => {
            // If response has new structure, extract the order
            if (orderItem.orderId) {
              return orderItem.orderId;
            }
            return orderItem;
          });
          setOrder(updatedOrders);
        }

        setMsg(res.data.message);
        setDialogBox(true);
        setTimeout(() => {
          setDialogBox(false);
          setMsg("");
        }, 3000);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Update error:", error);
        setMsg("Failed to update delivery. Please try again.");
        setDialogBox(true);
        setTimeout(() => {
          setDialogBox(false);
          setMsg("");
        }, 3000);
      });
  };

  const handleModal = (products) => {
    setProducts(products);
    setShow(true);
  };

  return (
    <>
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <LoadingSpinner />
        </div>
      )}

      <h2>
        Update Delivery Run {location?.state?.run}-{location?.state?.deliveryId}
      </h2>

      <SuccessDialogue show={dialogBox} msg={msg} />

      {show && (
        <ProductsModal show={show} setShow={setShow} products={products} />
      )}

      {/* Delivery Information Form */}
      <div className={styles.deliveryInfoSection}>
        <h3>Delivery Information</h3>
        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <label htmlFor="run">Run Name:</label>
            <input
              id="run"
              type="text"
              value={run}
              onChange={(e) => setRun(e.target.value)}
              placeholder="Enter run name"
              className={styles.formInput}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="vehicle">Vehicle Rego:</label>
            <input
              id="vehicle"
              type="text"
              value={vehicle}
              onChange={(e) => setVehicle(e.target.value)}
              placeholder="Enter vehicle rego"
              className={styles.formInput}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="deliveryDate">Delivery Date:</label>
            <input
              id="deliveryDate"
              type="date"
              value={deliveryDate}
              onChange={(e) => setDeliveryDate(e.target.value)}
              className={styles.formInput}
            />
          </div>

          {/* ✅ NEW: Primary Driver Selection */}
          <div className={styles.formGroup}>
            <label htmlFor="primaryDriver">Primary Driver:</label>
            <select
              id="primaryDriver"
              value={primaryDriver}
              onChange={handlePrimaryDriverChange}
              className={styles.formInput}
            >
              <option value="">Select Primary Driver</option>
              {availableUsers.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.name || user.email.split("@")[0]} ({user.email})
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="assignedUsers">Additional Assigned Drivers:</label>
            <select
              id="assignedUsers"
              multiple
              value={assignedUsers}
              onChange={handleAssignedUsersChange}
              className={styles.multiSelect}
              style={{ height: "120px" }}
            >
              {availableUsers.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.name || user.email.split("@")[0]} ({user.email})
                </option>
              ))}
            </select>
            <small className={styles.helpText}>
              Hold Ctrl (Cmd on Mac) to select multiple additional drivers
            </small>
          </div>

          <div className={styles.formGroup} style={{ gridColumn: "1 / -1" }}>
            <label htmlFor="comments">Delivery Instructions/Comments:</label>
            <textarea
              id="comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Enter delivery notes, special instructions, or comments for drivers"
              className={styles.formTextarea}
              rows="3"
            />
          </div>
        </div>
      </div>

      {/* Orders Management */}
      <div className={styles.ordersSection}>
        <div className={styles.sectionHeader}>
          <h3>Orders Management</h3>
          <label className={styles["toggle-switch"]}>
            <input
              type="checkbox"
              checked={addToRun}
              onChange={handleAddToRunClick}
            />
            <span className={styles["slider"]}></span>
            <span className={styles["button-text"]}>
              {addToRun ? "Remove Orders" : "Add Orders"}
            </span>
          </label>
        </div>

        <div className={styles.container}>
          <div>
            <h4>Current Orders in Run ({order.length})</h4>
            {order.length === 0 ? (
              <div className={styles.emptyState}>
                No orders in this run yet. Add some orders to get started.
              </div>
            ) : (
              <table className={styles.tableBody}>
                <thead>
                  <tr>
                    <th>Select</th>
                    <th>Order #</th>
                    <th>Customer</th>
                    <th>Items</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {order.map((orderItem, index) => (
                    <tr key={orderItem._id || index}>
                      <td>
                        <input
                          type="checkbox"
                          onChange={(e) =>
                            handleCheckbox(e, orderItem?.orderNumber)
                          }
                          value={orderItem?.orderNumber}
                          id={orderItem?.orderNumber}
                          checked={checkboxItems.includes(
                            orderItem?.orderNumber
                          )}
                        />
                      </td>
                      <td>{orderItem?.orderNumber}</td>
                      <td>{orderItem?.customer?.businessName}</td>
                      <td>
                        <button
                          className="popupBtn"
                          onClick={() => handleModal(orderItem?.products)}
                        >
                          {orderItem?.products?.length || 0} items
                        </button>
                      </td>
                      <td>
                        <span
                          className={`${styles.status} ${
                            styles[orderItem?.status?.toLowerCase()]
                          }`}
                        >
                          {orderItem?.status}
                        </span>
                      </td>
                      <td>
                        {orderItem?.date &&
                          new Date(orderItem.date).toLocaleDateString("en-GB")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {addToRun && (
            <>
              <button className={styles.addbutton} onClick={addOrderToRun}>
                Add Selected
                <br />
                &#60;
              </button>

              <div>
                <h4>Available Pending Orders</h4>
                {pendingOrders.length <= 0 ? (
                  <div className={styles.emptyState}>
                    No pending orders available to add.
                  </div>
                ) : (
                  <table className={styles.tableBody}>
                    <thead>
                      <tr>
                        <th>Select</th>
                        <th>Order #</th>
                        <th>Customer</th>
                        <th>Items</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingOrders.map((orderItem, index) => (
                        <tr key={orderItem._id || index}>
                          <td>
                            <input
                              type="checkbox"
                              onChange={(e) =>
                                handleCheckbox(e, orderItem?.orderNumber)
                              }
                              value={orderItem?.orderNumber}
                              id={`pending-${orderItem?.orderNumber}`}
                              checked={checkboxItems.includes(
                                orderItem?.orderNumber
                              )}
                            />
                          </td>
                          <td>{orderItem?.orderNumber}</td>
                          <td>{orderItem?.customer?.businessName}</td>
                          <td>
                            <button
                              className="popupBtn"
                              onClick={() => handleModal(orderItem?.products)}
                            >
                              {orderItem?.products?.length || 0} items
                            </button>
                          </td>
                          <td>
                            {orderItem?.date &&
                              new Date(orderItem.date).toLocaleDateString("en-GB")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className={styles.actionButtons}>
        {addToRun ? (
          <button
            onClick={updateDelivery}
            className="confirmBtn"
            disabled={loading}
          >
            {loading ? "Updating..." : "💾 Update Delivery"}
          </button>
        ) : (
          <div className={styles.buttonGroup}>
            <button
              className="confirmBtn"
              onClick={removeFromRun}
              disabled={checkboxItems.length === 0 || loading}
            >
              🗑️ Remove Selected Orders
            </button>
            <button
              onClick={updateDelivery}
              className="confirmBtn"
              disabled={loading}
            >
              {loading ? "Updating..." : "💾 Update Delivery"}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default UpdateDeliveries;
